import { AppMenuItem, GuardedItem, MenuItemGuard } from '@pmp/components';
import { checkPermissions, checkFlags } from '@pmp/utils';
import { FeatureFlag, GuardOptions, Permission, Role, WildPermission } from './interfaces';

export const PERMISSIONS: Record<string, string[]> = {
  lenders: ['view', 'create', 'update', 'delete', 'review'],
  firms: ['view', 'create', 'update', 'delete', 'review'],
  brokers: ['view', 'create', 'update', 'delete', 'review'],
  procurationFees: ['view', 'create', 'update', 'delete', 'review'],

  products: ['view', 'create', 'update', 'review'],
  baseRates: ['view', 'create', 'update', 'delete', 'review'],
  exchangeRates: ['view', 'create', 'update', 'delete', 'review'],
  ercProfiles: ['view', 'create', 'update', 'delete', 'review'],
  fees: ['view', 'create', 'update', 'delete', 'review'],
  feeProfiles: ['view', 'create', 'update', 'delete', 'review'],
  exclusivitySets: ['view', 'create', 'update', 'delete', 'review'],

  imports: ['view', 'create', 'update', 'delete'],
  exportTemplates: ['view', 'create', 'update', 'delete'],
  changeRequests: ['view'],
} as const;

export const permissionByRole: Record<Role, Permission[]> = {
  PMP_ReadOnly: [
    'products:view',
    'baseRates:view',
    'exchangeRates:view',
    'ercProfiles:view',
    'fees:view',
    'feeProfiles:view',
    'exclusivitySets:view',

    'imports:view',
    'exportTemplates:view',
  ],
  PMP_Update: [
    'products:view',
    'products:create',
    'products:update',
    'baseRates:view',
    'baseRates:create',
    'baseRates:update',
    'baseRates:delete',
    'exchangeRates:view',
    'exchangeRates:create',
    'exchangeRates:update',
    'exchangeRates:delete',
    'ercProfiles:view',
    'ercProfiles:create',
    'ercProfiles:update',
    'ercProfiles:delete',
    'fees:view',
    'fees:create',
    'fees:update',
    'fees:delete',
    'feeProfiles:view',
    'feeProfiles:create',
    'feeProfiles:update',
    'feeProfiles:delete',
    'exclusivitySets:view',
    'exclusivitySets:create',
    'exclusivitySets:update',
    'exclusivitySets:delete',

    'imports:view',
    'imports:create',
    'imports:update',
    'imports:delete',
    'exportTemplates:view',
    'exportTemplates:create',
    'exportTemplates:update',
    'exportTemplates:delete',
    'changeRequests:view',
  ],
  PMP_Reviewers: [
    'lenders:review',
    'firms:review',
    'brokers:review',
    'procurationFees:review',

    'products:review',
    'baseRates:review',
    'exchangeRates:review',
    'ercProfiles:review',
    'fees:review',
    'feeProfiles:review',
    'exclusivitySets:review',

    'exportTemplates:review',
    'changeRequests:view',
  ],
  PMP_Lenders: [
    'lenders:view',
    'lenders:create',
    'lenders:update',
    'lenders:delete',
    'firms:view',
    'firms:create',
    'firms:update',
    'firms:delete',
    'brokers:view',
    'brokers:create',
    'brokers:update',
    'brokers:delete',
    'procurationFees:view',
    'procurationFees:create',
    'procurationFees:update',
    'procurationFees:delete',
    'changeRequests:view',
  ],
};

export const permissionMatches = (userPermissions: Set<Permission>, neededPermission: Permission | WildPermission): boolean => {
  if (userPermissions.has(neededPermission as Permission)) return true;

  const i = (neededPermission as string).indexOf(':');
  if (i < 0) return false;
  const res = (neededPermission as string).slice(0, i);
  const act = (neededPermission as string).slice(i + 1);

  if (userPermissions.has(`${res}:*` as Permission)) return true;

  if (act === '*') {
    for (const up of userPermissions) {
      if (up.startsWith(res + ':')) return true;
    }
  }

  return false;
};

export const getUserGroups = (groups?: string) => {
  if (!groups) return [];

  // Try to parse as JSON (array or quoted string)
  // Cognito stores single groups as JSON strings e.g. "PMP_Lenders"
  // and multiple groups as JSON arrays e.g. ["PMP_Update","PMP_Reviewers"]
  if (groups.startsWith('[') || groups.startsWith('"')) {
    try {
      const parsed = JSON.parse(groups);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'string') return [parsed];
    } catch {
      if (groups.startsWith('[')) {
        // Legacy format: [Group1, Group2] (without quotes inside brackets)
        return groups.slice(1, -1).split(', ');
      }
    }
  }

  // Plain string (no JSON encoding)
  return [groups];
};

const passesGuard = (
  guard: MenuItemGuard | undefined,
  permissionsSet: Set<Permission>,
  flagsSet: Set<FeatureFlag>,
  { permissionsOperator = 'OR', flagsOperator = 'OR' }: GuardOptions,
) => {
  if (!guard) return true;

  const permsOk = checkPermissions({
    permissions: guard.permissions,
    userPermissions: permissionsSet,
    permissionsOperator,
  });

  const flagsOk = checkFlags({
    flags: guard.flags,
    envFeatureFlags: flagsSet,
    flagsOperator,
  });

  return permsOk && flagsOk;
};

const pruneDividers = (items: AppMenuItem[]) => {
  const out: AppMenuItem[] = [];
  for (const it of items) {
    if (it?.type === 'divider') {
      if (!out.length) continue;
      if (out[out.length - 1]?.type === 'divider') continue;
    }
    out.push(it);
  }
  if (out[out.length - 1]?.type === 'divider') out.pop();
  return out;
};

export function filterMenuByGuard(
  items: GuardedItem[],
  userPermissions: Set<Permission>,
  envFeatureFlags: Set<FeatureFlag>,
  options: GuardOptions = {},
): AppMenuItem[] {
  const mapOne = (item: GuardedItem): AppMenuItem | null => {
    const children = item.children?.map(mapOne).filter(Boolean);
    const selfOk = passesGuard(item.guard, userPermissions, envFeatureFlags, options);

    if (children?.length) {
      const { guard, ...rest } = item as any;
      return { ...rest, children } as AppMenuItem;
    }

    if (!selfOk) return null;
    const { guard, ...rest } = item as any;
    return rest as AppMenuItem;
  };

  const filtered = items.map(mapOne).filter(Boolean);
  return pruneDividers(filtered);
}
